import moment from 'moment';
import React from 'react';
import { SingleDatePicker } from "react-dates";

import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import './date.css';
import { classnames } from '../../../utils/classnames';
import { CustomInputDate } from './customInputDate';
import { isDescendant } from '../../../utils/utils';
import { NavArrow, SelectableDay, SelectMonthYear } from './accessibilityUtils';

export function formatLocale(locale) {
	let _l = locale.replace("_", "-");
	if (_l.toLowerCase() === "en-gb") {
		return "en";
	}
	return _l;
}

function getOptionsYears(minYear, maxYear) {
	return Array.apply(0, Array(maxYear - minYear + 1)).map(function(_, i) {
		return {
			label: moment()
				.year(minYear + i)
				.format("YYYY"),
			value: moment()
				.year(minYear + i)
				.format("YYYY")
		};
	});
}

function isBeforeDay(a, b, offset = 0) {
	if (!moment.isMoment(a) || !moment.isMoment(b)) return false;

	const aYear = a.year();
	const aMonth = a.month();

	const bYear = b.year();
	const bMonth = b.month();

	const isSameYear = aYear === bYear;
	const isSameMonth = aMonth === bMonth;
	if (isSameYear && isSameMonth) return a.date() < b.date() + offset;
	if (isSameYear) return aMonth < bMonth;
	return aYear < bYear;
}

function isAfterDay(a, b) {
	if (!moment.isMoment(a) || !moment.isMoment(b)) return false;
	// opposite of < is >= and we want > so we add 1 day to include the maxDate as an option
	return !isBeforeDay(a, b, 1);
}

function formatIsoDateStringToLocalDateString(value, locale, longDateFormat, inputFormat) {
	moment.locale(locale);
	if (inputFormat) {
		return moment(value, inputFormat).format(moment.localeData().longDateFormat(longDateFormat))
	}
	return moment(value).format(moment.localeData().longDateFormat(longDateFormat))
}

export function DateInput({
	id,
	value = "",
	onChange,
	locale = 'en',
	placeholder = null,
	disabled = false,
	required = false,
	errors=null,
	minYear = 1920,
	maxYear = 2060,
	minDate = null,
	maxDate = null,
	onClick,
	inputFormat = null,
	outputFormat = null,
	longDateFormat = 'L',
	className = '',
	ariaDescribedBy,
	autoComplete = null,
	...rest
}) {

	//Locale compatibility
	const _locale = React.useMemo(() => formatLocale(locale), [locale]);

	const [ariaLabelAnnouncer, setAriaLabelAnnouncer] = React.useState('');
	const [focus, setFocus] = React.useState(false);
	const [inputInternalValue, setInputInternalValue] = React.useState(value ? formatIsoDateStringToLocalDateString(value, _locale, longDateFormat, inputFormat) : '');
	const inputRef = React.useRef(null);
	const dateContainerRef = React.useRef(null);
	const datePickerRef = React.useRef(null);
	const [previousLocale, setPreviousLocale] = React.useState(null);

	// when clicking on the input it triggers onFocus so to avoid triggering twice we store a state
	const [ignoreFromFocus, setIgnoreFromFocus] = React.useState(false);

	// convert String to moment date
	const getMomentDate = React.useCallback(dateValue => {
		if (typeof dateValue !== 'string') return dateValue;
		if (moment(dateValue, moment.ISO_8601).isValid()) {
			return moment(dateValue);
		} else if (moment(dateValue, moment.localeData().longDateFormat(longDateFormat)).isValid()) {
			return moment(dateValue, moment.localeData().longDateFormat(longDateFormat), false);
		} else if (moment(dateValue, 'DD/MM/YYYY').isValid()) {
			const [d, m, y] = dateValue.split('/');
			return moment(`${y}-${m}-${d}`);
		}
		return null;
	}, [moment, longDateFormat]);

	// convert moment date to String
	const getStringDate = React.useCallback(dateMoment => {
		if (!dateMoment) return "";
		if (typeof dateMoment === 'string') return dateMoment;
		return dateMoment.format(moment.localeData().longDateFormat(longDateFormat));
	}, [moment, longDateFormat])

	//When changing locale we want to update the format of the value (ex: DD/MM/YYYY > MM/DD/YYYY)
	const updateFormatValue = React.useCallback((newLocale) => {
		if (!inputInternalValue || !previousLocale) return;
		const momentDate = moment(inputInternalValue, moment.localeData(previousLocale).longDateFormat(longDateFormat));
		moment.locale(newLocale);
		const newValue = momentDate?.format(moment.localeData().longDateFormat(longDateFormat));
		setInputInternalValue(newValue);
	}, [inputInternalValue, longDateFormat, previousLocale]);

	//Refresh moment with current locale
	React.useEffect(() => {
		moment.locale(_locale);
		updateFormatValue(_locale);
		setPreviousLocale(_locale);
	}, [_locale]);

	// used for placeholder and calendar default value (otherwise 1920 is selected)
	const latestDateAllowed = React.useMemo(() => {
		const currentDate = moment();
		const maxDateMoment = getMomentDate(maxDate);
		const minDateMoment = getMomentDate(minDate);
		let _latestDate = currentDate;
		if (minDate && isBeforeDay(_latestDate, minDateMoment)) {
			_latestDate = minDateMoment;
		}

		if (maxDate && isAfterDay(_latestDate, maxDateMoment)) {
			_latestDate = maxDateMoment;
		}

		return _latestDate;
	}, [maxDate, minDate, getMomentDate]);

	const _placeholder = React.useMemo(() => {
		moment.locale(_locale); //important for init locale for placeholder
		return latestDateAllowed.format(moment.localeData().longDateFormat(longDateFormat));
	}, [_locale, latestDateAllowed, longDateFormat])

	// hide SingleDatePicker default unused input
	React.useEffect(() => {
		if (dateContainerRef?.current) {
			const screenReaderMessage = dateContainerRef.current.querySelector('#DateInput__screen-reader-message-date');
			screenReaderMessage.setAttribute('aria-hidden', true);
		}
	}, [dateContainerRef?.current]);

	const _minYear = React.useMemo(() => minDate ? moment(minDate).year() : minYear,[minYear, minDate])
	const _maxYear = React.useMemo(() => maxDate ? moment(maxDate).year() : maxYear,[maxYear, maxDate])

	const years = React.useMemo(() => {
		return getOptionsYears(_minYear, _maxYear);
	}, [_minYear, _maxYear]);
	
	const _onChange = React.useCallback(
		date => {
			setAriaLabelAnnouncer(date);
			let _date = date;
			if (!_date) {
				return onChange('');
			}

			if (outputFormat === 'local') {
				return onChange(_date);
			}

			const momentDate = moment(date, moment.localeData(_locale).longDateFormat(longDateFormat));
			if (outputFormat) {
				return onChange(momentDate.format(outputFormat))
			}

			return onChange(momentDate.format())
		},
		[onChange, _locale, outputFormat, longDateFormat]
	);
	
	// DATE validity
	// disable the days that are before the minDate or after the maxDate
	const isOutsideRange = React.useCallback(
		day => {
			return (
				isBeforeDay(day, minDate ? getMomentDate(minDate) : null) ||
				isAfterDay(day, maxDate ? getMomentDate(maxDate) : null) ||
				(minYear && day.year < minYear) || (maxYear && day.year > maxYear)
			);
		},
		[minDate, maxDate, minYear, maxYear]
	);

	const updateIfValid = React.useCallback(
		newDate => {
			if (minDate && isBeforeDay(newDate, getMomentDate(minDate)))
				newDate = getMomentDate(minDate);
			if (minYear && newDate?.year() < minYear)
				newDate = newDate?.year(minYear);
			if (maxDate && isAfterDay(newDate, getMomentDate(maxDate)))
				newDate = getMomentDate(maxDate);
			if (maxYear && newDate?.year() > maxYear)
				newDate = newDate?.year(maxYear);
			let valid = newDate?.isValid();
			_onChange(valid ? getStringDate(newDate) : null);
			setInputInternalValue(valid ? getStringDate(newDate) : null);
		},
		[_onChange, getStringDate, getMomentDate, minDate, maxDate, minYear, maxYear]
	)

	const formatWhenLosingFocus = React.useCallback(() => {
			const _date = getMomentDate(inputInternalValue);
			updateIfValid(_date);
		}
	, [inputInternalValue, getMomentDate, updateIfValid]);
	
	// FOCUS (focus the input when clicking on multiple components and the calendar)
	
	const focusInput = React.useCallback((isFocus = true) => {
		if (isFocus)
			inputRef?.current?.focus();
		else
			inputRef?.current?.blur();
	}, [inputRef]);

	// handle focus across multiple components
	const multiComponentFocus = React.useCallback((fromFocus = false) => {
		setIgnoreFromFocus(true);
		if (ignoreFromFocus && fromFocus) return;
		setFocus(!focus);
		// we switch component show + blur the input when hidding
		if (!focus) {
			focusInput();
		}
	}, [focus, ignoreFromFocus]);

	// keep focus on input if blur on calendar button
	const multiComponentBlur = React.useCallback((event) => {
		const isRelated = isDescendant(dateContainerRef.current, event?.relatedTarget);
		const isDay = event?.relatedTarget?.className?.includes('CalendarDay');
		const isClearButton = event?.relatedTarget?.className?.includes('SingleDatePickerInput_clearDate');
		if (event?.relatedTarget && !isDay && !isClearButton && isRelated)
			focusInput();
		else if (!isDay) {
			// clicked outside
			focusInput(false);
			setFocus(false);
			setIgnoreFromFocus(false);
		}
	}, [dateContainerRef]);

	//load external value
	React.useEffect(() => {
		if (formatIsoDateStringToLocalDateString(value, _locale, longDateFormat, inputFormat) !== inputInternalValue) {
			setInputInternalValue(value ? formatIsoDateStringToLocalDateString(value, _locale, longDateFormat, inputFormat) : '');
		}
	}, [value, _locale, longDateFormat, inputFormat]);

	return (
		<div ref={dateContainerRef} className={classnames(className, 'neo-date-container', (focus && 'focused'))}>
			<CustomInputDate
				id={id}
				minYear={_minYear}
				maxYear={_maxYear}
				maxMonth={maxDate ? getMomentDate(maxDate)?.month : null}
				minMonth={minDate ? getMomentDate(minDate)?.month : null}
				required={required}
				errors={errors}
				onBlur={multiComponentBlur}
				onClick={multiComponentFocus}
				inputRef={inputRef}
				value={inputInternalValue || ''}
				setValue={setInputInternalValue}
				placeholder={placeholder || _placeholder}
				dateFormat={_placeholder}
				formatWhenLosingFocus={formatWhenLosingFocus}
				getMomentDate={getMomentDate}
				longDateFormat={longDateFormat}
				ariaDescribedBy={ariaDescribedBy}
				ariaLabelAnnouncer={ariaLabelAnnouncer}
				autoComplete={autoComplete}
				disabled={disabled}
			/>
			<SingleDatePicker
				small
				block
				ref={datePickerRef}
				ariaLabel={"Calendar"}
				disabled={disabled}
				displayFormat={() => moment.localeData().longDateFormat(longDateFormat)}
				showClearDate={Boolean(!disabled && value)}
				aria-describedby={null}
				keepFocusOnInput={true}
				date={(value ? getMomentDate(value) : latestDateAllowed) || null}
				onDateChange={(date) => {updateIfValid(date); multiComponentBlur()}}
				onFocusChange={() => {}}
				readOnly
				navPrev={<NavArrow direction={'left'} />}
				navNext={<NavArrow direction={'right'} />}
				focused={focus}
				numberOfMonths={1}
				isOutsideRange={isOutsideRange}
				placeholder={''}
				renderDayContents={day => (
					<SelectableDay day={day} onBlur={multiComponentBlur} />
				)}
				renderWeekHeaderElement={day => (
					<small>{day.charAt(0).toUpperCase()}</small>
				)}
				renderMonthElement={({
					month,
					onMonthSelect,
					onYearSelect
				}) => (
					<SelectMonthYear
						month={month}
						onMonthSelect={onMonthSelect}
						onYearSelect={onYearSelect}
						years={years}
						callback={() => setTimeout(() => focusInput(), 300)}
					/>
				)}
				{...rest}
			/>
		</div>
	)
}